import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { DashboardStats, PoolInfo, AlgorithmProfitability, MinerStatus } from '../types';

// State interface
interface AppState {
  dashboardStats: DashboardStats | null;
  pools: PoolInfo[];
  profitability: AlgorithmProfitability[];
  miners: MinerStatus[];
  loading: {
    dashboard: boolean;
    pools: boolean;
    profitability: boolean;
    miners: boolean;
  };
  errors: {
    dashboard: string | null;
    pools: string | null;
    profitability: string | null;
    miners: string | null;
  };
  settings: {
    refreshInterval: number;
    theme: 'light' | 'dark';
    autoRefresh: boolean;
  };
}

// Action types
type AppAction =
  | { type: 'SET_DASHBOARD_STATS'; payload: DashboardStats }
  | { type: 'SET_POOLS'; payload: PoolInfo[] }
  | { type: 'SET_PROFITABILITY'; payload: AlgorithmProfitability[] }
  | { type: 'SET_MINERS'; payload: MinerStatus[] }
  | { type: 'SET_LOADING'; payload: { key: keyof AppState['loading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof AppState['errors']; value: string | null } }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<AppState['settings']> }
  | { type: 'CLEAR_ALL_ERRORS' };

// Initial state with mock data for demonstration
const initialState: AppState = {
  dashboardStats: {
    totalPools: 12,
    totalWorkers: 1847,
    totalHashrate: 156780000000000, // 156.78 TH/s
    activeAlgorithms: 4,
    topProfitableAlgorithm: 'SHA256'
  },
  pools: [],
  profitability: [],
  miners: [],
  loading: {
    dashboard: false,
    pools: false,
    profitability: false,
    miners: false,
  },
  errors: {
    dashboard: null,
    pools: null,
    profitability: null,
    miners: null,
  },
  settings: {
    refreshInterval: 30000,
    theme: 'dark',
    autoRefresh: true,
  },
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_DASHBOARD_STATS':
      return { ...state, dashboardStats: action.payload };
    
    case 'SET_POOLS':
      return { ...state, pools: action.payload };
    
    case 'SET_PROFITABILITY':
      return { ...state, profitability: action.payload };
    
    case 'SET_MINERS':
      return { ...state, miners: action.payload };
    
    case 'SET_LOADING':
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value },
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        errors: { ...state.errors, [action.payload.key]: action.payload.value },
      };
    
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };
    
    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        errors: {
          dashboard: null,
          pools: null,
          profitability: null,
          miners: null,
        },
      };
    
    default:
      return state;
  }
};

// Context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;